import React from "react";
import _ from "lodash";
import { ProjectIcon } from "@netapp/bxp-design-system-react/icons/monochrome";
import { CompanyIcon, FolderIcon, LightIcon } from "@netapp/bxp-style/react-icons/General"
import { ESeriesIcon, StoragegridIcon } from "@netapp/bxp-style/react-icons/Logo";
import { ReactComponent as OnPremIcon } from "../images/OnPrem.svg";
import { NotAvailableCellData, StyledLink, TextWithOverflow, withTextOverflow } from "@netapp/bxp-design-system-react";
import { isNcl } from "utils/app/utils";
import { DECIMAL_SIZE_UNITS, formatSize, UNIT_OF } from "utils/decimalUnits";
import { formatSize as formatBytes } from "utils/units";
import styles from "../modules/app/components/MainHeader.module.scss";

const OverflowStyledLink = withTextOverflow(StyledLink);

export const getProjectIcon = () => isNcl() ? LightIcon : ProjectIcon;

// Renders the appropriate scope icon (Company/Folder/Project) based on resourceType.
export const ScopeTypeIcon = ({ resourceType, className, ...props }) => {
    const ResolvedProjectIcon = getProjectIcon();
    switch (resourceType) {
        case "organization":
            return <CompanyIcon className={className} {...props} />;
        case "folder":
            return <FolderIcon className={className} {...props} />;
        case "project":
            return <ResolvedProjectIcon className={className} {...props} />;
        default:
            return null;
    }
}

// Icon renderer for tree/accordion components with element.resourceType and element.isSelected.
export const createScopeIconRenderer = (unselectedClassName) => (element) => {
    const className = element?.isSelected ? "" : unselectedClassName;
    return <ScopeTypeIcon resourceType={element.resourceType} className={className} width="20" />;
}

export const IAMResourceIconRenderer = createScopeIconRenderer(styles["tenancy-icon-color"]);

const SYSTEM_TYPE_ICONS = {
    ONTAP: OnPremIcon,
    ONPREM: OnPremIcon,
    ESERIES: ESeriesIcon,
    STORAGEGRID: StoragegridIcon
};

const SYSTEM_TYPES = {
    OVERVIEW: 'overview',
    ESERIES: 'eseries',
    ONTAP: 'ontap',
    ONPREM: 'ontap',
    STORAGEGRID: 'storageGrid'
};

// Renders the ONTAP, on-premises, E-Series, or StorageGRID icon and label, using the caller's t and hiding the label when showLabel is false.
export const SystemTypeRenderer = ({ value, showLabel = true, t }) => {
    const systemType = _.toUpper(value);
    const labelKey = SYSTEM_TYPES[systemType];
    const Icon = SYSTEM_TYPE_ICONS[systemType];
    if (!labelKey) {
        return <NotAvailableCellData textToDisplay={t("not_available")} />;
    }
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            {/* Icon-only size comes from the parent .type-icon rule (30px). Labeled mode sets 24px here. */}
            {Icon === OnPremIcon
                ? <OnPremIcon style={{ filter: "grayscale(1)", ...(showLabel && { width: 24, height: 10 }) }} />
                : Icon ? (showLabel ? <Icon size={24} /> : <Icon />) : null}
            {showLabel && <TextWithOverflow>{t(labelKey)}</TextWithOverflow>}
        </span>
    );
};

export const StyledLinkWithOverflow = ({ className, children, to, ...props }) => {
    return (
        <OverflowStyledLink
            to={to}
            variant="text"
            className={className}
            style={{ display: "inline-block" }}
            {...props}
        >
            {children}
        </OverflowStyledLink>
    );
};



export const metricDataFormatter = (value, metric, highestUnit) => {
    switch (metric) {
        case "latency":
            if (typeof value !== 'number' || isNaN(value) || value < 0) {
                return null;
            }
            if (value === 0) {
                return `0`;
            }
            if (value > 1000) {
                return `${Number((value / 1000).toFixed(2))} ms`;
            }
            return `${Number((value / 1000).toFixed(3))} ms`;
        case UNIT_OF.THROUGHPUT:
            return formatSize(value, UNIT_OF.THROUGHPUT, DECIMAL_SIZE_UNITS.B, true, true);
        case UNIT_OF.IOPS:
            let unit = DECIMAL_SIZE_UNITS.B;
            if ((typeof value !== 'number' && isNaN(value) && value < 0)) {
                unit = value < 1000 ? DECIMAL_SIZE_UNITS.B : DECIMAL_SIZE_UNITS.KB;
            }
            return formatSize(value, UNIT_OF.IOPS, unit, true, true);
     case UNIT_OF.CAPACITY:
         // highestUnit is a unit object from utils/units (e.g., { unit: 'TiB', size: ... })
         // use the byte-aware formatter from utils/units to render capacity correctly
         return formatBytes(value, highestUnit, false, true);
        default:
            return value;
    }
}